import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { OwnerLoginSchema, type OwnerLoginInput } from "@steelbill/types"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"

/**
 * Sign in — shadcn's `login-04` block.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * VALIDATION USES THE SERVER'S OWN SCHEMA. `OwnerLoginSchema` comes from
 * `packages/types` and is the exact object `POST /auth/owner-login` validates
 * with. The client cannot drift from the server about what a valid phone number
 * or PIN is, because there is only one definition of it.
 *
 * `noValidate` ON THE FORM IS DELIBERATE. Without it the browser shows its own
 * validation bubble — a grey tooltip reading "Please fill out this field" in
 * Chrome's font, in the browser's UI language rather than the app's, positioned
 * wherever Chrome likes. It cannot be styled, cannot be translated (§11.4), and
 * on a phone it appears and vanishes before the owner has read it. Turning it
 * off and rendering `FieldError` instead is what makes the message ours.
 *
 * TWO KINDS OF ERROR, TWO PLACES. A field problem ("this PIN is not six
 * digits") belongs beside the field, in `FieldError`. A server refusal ("that
 * account is locked for 15 minutes") belongs at the top in an `Alert`, because
 * it is about the attempt rather than about one input.
 *
 * WHAT WAS REMOVED FROM THE BLOCK, and each had to go:
 *   • THE APPLE / GOOGLE / META BUTTONS — §2 defines exactly two ways in, phone
 *     + PIN and email + password. There is no OAuth. Three buttons that cannot
 *     sign anyone in are worse than blank space: the owner taps Google, nothing
 *     happens, and he decides the app is broken.
 *   • "FORGOT YOUR PASSWORD?" — there is no self-serve reset; a forgotten PIN is
 *     reset by running `create-user.ts` on the server.
 *   • "SIGN UP" — two accounts, both admin-created (§2).
 *   • TERMS AND PRIVACY — neither page exists.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface LoginFormProps extends Omit<React.ComponentProps<"div">, "onSubmit"> {
  staySignedIn: boolean
  onStayChange: (value: boolean) => void
  busy: boolean
  /** The server's own sentence when it has one; null otherwise. */
  error: string | null
  onSubmit: (values: OwnerLoginInput) => void
}

export function LoginForm({
  className,
  staySignedIn,
  onStayChange,
  busy,
  error,
  onSubmit,
  ...props
}: LoginFormProps) {
  const { t } = useTranslation()

  const form = useForm<OwnerLoginInput>({
    resolver: zodResolver(OwnerLoginSchema),
    /*
     * `onTouched`, not `onChange`: validating every keystroke means the PIN
     * field reads "must be 6 digits" from the first character typed, which is
     * true and useless. Waiting until the field is left says it once, when it
     * is actionable.
     */
    mode: "onTouched",
    defaultValues: { phone: "", pin: "" },
  })

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">{t("auth.title")}</h1>
                <p className="text-balance text-muted-foreground">{t("auth.subtitle")}</p>
              </div>

              {error === null ? null : (
                <Alert variant="destructive">
                  <AlertTitle>{t("auth.couldNotSignIn")}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Field data-invalid={form.formState.errors.phone !== undefined}>
                <FieldLabel htmlFor="phone">{t("lbl.phone")}</FieldLabel>
                <Input
                  id="phone"
                  type="tel"
                  /* `inputMode="numeric"` raises the number pad without
                   * `type="number"`, which would strip a leading zero and put
                   * spinner arrows on a phone number. */
                  inputMode="numeric"
                  autoComplete="username"
                  aria-invalid={form.formState.errors.phone !== undefined}
                  {...form.register("phone")}
                />
                {form.formState.errors.phone === undefined ? null : (
                  <FieldError>{t("err.phoneInvalid")}</FieldError>
                )}
              </Field>

              <Field data-invalid={form.formState.errors.pin !== undefined}>
                <FieldLabel htmlFor="pin">{t("lbl.pin")}</FieldLabel>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="current-password"
                  aria-invalid={form.formState.errors.pin !== undefined}
                  {...form.register("pin")}
                />
                {form.formState.errors.pin === undefined ? null : (
                  <FieldError>{t("err.pinInvalid")}</FieldError>
                )}
              </Field>

              <Field>
                <div className="flex items-center justify-between gap-4">
                  <FieldLabel htmlFor="stay" className="font-normal">
                    {t("lbl.staySignedIn")}
                  </FieldLabel>
                  <Switch id="stay" checked={staySignedIn} onCheckedChange={onStayChange} />
                </div>
                <FieldDescription>{t("auth.staySignedInHelp")}</FieldDescription>
              </Field>

              <Field>
                <Button type="submit" disabled={busy}>
                  {busy ? t("auth.signingIn") : t("btn.signIn")}
                </Button>
              </Field>
            </FieldGroup>
          </form>

          {/*
            * The block's second panel. It ships a photograph; this app has none,
            * and a stock image on the sign-in screen of a working tool is
            * decoration pretending to be content. The letterhead mark instead —
            * the one place §12.4's plum appears anywhere in the app.
            *
            * `hidden md:flex` is the block's own rule and it is right: on a
            * phone this panel would push the PIN field below the fold, and the
            * phone is where the owner actually signs in.
            */}
          <div className="relative hidden items-center justify-center bg-muted md:flex">
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <span
                aria-hidden="true"
                className="flex size-14 items-center justify-center rounded-xl bg-brand text-2xl font-bold text-brand-foreground"
              >
                {[...t("app.name")][0] ?? ""}
              </span>
              <span className="text-lg font-semibold tracking-tight">{t("app.name")}</span>
              <span className="max-w-[16rem] text-balance text-sm text-muted-foreground">
                {t("app.tagline")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
